import jQuery from 'jquery';
import tinycolor2 from 'tinycolor2';

import imgCleaner from 'ub/ui/inline-text/img-cleaner';
import backgroundStyleCleaner from 'ub/ui/inline-text/background-style-cleaner';
import manualFontSetter from 'ub/ui/inline-text/manual-font-setter';

import { paragraphStyles } from '../style-tab-content-components/text-panel-defaults';

export default class InlineTextStyleAPI {
  constructor(froalaEditor) {
    this.froalaEditor = froalaEditor;
    this.froalaEditorContents = froalaEditor.el;
  }

  _froalaEditorCommand(...froalaCommandArgs) {
    return this.froalaEditor.$box.froalaEditor(...froalaCommandArgs);
  }

  _set(...froalaCommandArgs) {
    if (this.isFocused()) {
      // If froala is currently active, apply a command to the current user selection.
      this._froalaEditorCommand(...froalaCommandArgs);
    } else {
      // Otherwise, apply the command to the entire contents by temporarily selecting all. This
      // seems hacky, but the froala API only seems to provide a way to run commands against the
      // current selection, not against the full contents of the editor.
      const currentScrollTop = window.editor.canvasBody.e.scrollTop;

      this._froalaEditorCommand('edit.on');
      this._froalaEditorCommand('commands.selectAll');

      // The above selectAll command causes Froala to scroll the canvas to bring the text element
      // into view. We restore the previous scroll position to prevent this jumping from happening.
      window.editor.canvasBody.e.scrollTop = currentScrollTop;

      this._froalaEditorCommand(...froalaCommandArgs);

      this._froalaEditorCommand('selection.clear');
      this._froalaEditorCommand('edit.off');
    }
  }

  isFocused() {
    return this.froalaEditor.selection.inEditor();
  }

  getSelectedElement() {
    return jQuery(this._froalaEditorCommand('selection.element'));
  }

  _getActiveElement() {
    return this.isFocused() ? this.getSelectedElement() : this._getFirstElement();
  }

  _getFirstElement() {
    // Find the first piece of text (text node) in the editor and return the element that that node
    // belongs to, as a jQuery object.
    return jQuery(
      document.createTreeWalker(this.froalaEditorContents, NodeFilter.SHOW_TEXT).nextNode()
    ).parent() || null;
  }

  _getBlockElementsForGet() {
    // When focused, return the selected block element (heading, paragraph, etc).
    // When not focused, return the first block element that contains a text node.
    if (this.isFocused()) {
      return jQuery(this.froalaEditor.selection.blocks());
    } else {
      const $textElement = this._getFirstElement();

      return jQuery(
        !$textElement || this.froalaEditor.node.isBlock($textElement[0]) ?
          $textElement :
          this.froalaEditor.node.blockParent($textElement[0])
      );
    }
  }

  _getBlockElementsForSet() {
    // When focused, return the selected block element (heading, paragraph, etc).
    // When not focused, return all block elements.
    if (this.isFocused()) {
      return jQuery(this.froalaEditor.selection.blocks());
    } else {
      return jQuery(this.froalaEditorContents)
        .find('*')
        .filter((index, element) => this.froalaEditor.node.isBlock(element));
    }
  }

  _getCssValue(property) {
    // We look for explicit inline declarations of the property rather than using the computed value
    // because we only want to return a value if the user has explicitly set a value for the
    // property. For example if the user clicks the 'Bold' button, Froala wraps their selection in a
    // <strong> tag rather than setting a font-weight: 700 style. getFontWeight() should then return
    // `null` and getTextStyles().strong should be `true`.
    const $activeElement = this._getActiveElement();

    return [
      $activeElement[0],
      ...$activeElement.parentsUntil(this.froalaEditorContents).toArray(),
    ]
      .map(element => element.style[property])
      .filter(Boolean)[0] || null;
  }

  _getCssValueFromBlockElement(property) {
    const blockElement = this._getBlockElementsForGet()[0];
    return blockElement ? blockElement.style[property] : null;
  }

  sendContentChangedEvent() {
    // We call this for any style changes that do not trigger the 'contentChanged' froala event by
    // default. The third argument forces the event to be triggered even if froala is not active,
    // which is necessary when the user makes changes outside of edit mode.
    this.froalaEditor.events.trigger('contentChanged', [], true);
  }

  saveSelection() {
    // Only save selection if you are focused into the froala editor
    if (this.isFocused()) {
      // We are hiding the markers here to prevent froala from removing selection markers on blur
      // See notes/pb-2507.md for context
      this._froalaEditorCommand('popups.hideAll');

      this._froalaEditorCommand('selection.save');
    }
  }

  restoreSelection() {
    this._froalaEditorCommand('selection.restore');
  }

  focus() {
    this._froalaEditorCommand('events.focus');
  }

  toggleTextStyle(property) {
    const froalaCommand = {
      strong: 'bold',
      em: 'italic',
      u: 'underline',
      s: 'strikeThrough',
      sup: 'superscript',
      sub: 'subscript',
    }[property];

    this._set(`commands.${froalaCommand}`);
  }

  setAlignment(alignment) {
    this._set('align.apply', alignment);
    this.sendContentChangedEvent();
  }

  setListStyle(listStyle) {
    this._set('lists.format', listStyle);
    this.sendContentChangedEvent();
  }

  setLineHeight(lineHeight) {
    this._getBlockElementsForSet().css('line-height', `${lineHeight}px`);
    this.sendContentChangedEvent();
  }

  _getLinkAttr(attrName) {
    const linkNode = this._froalaEditorCommand('link.get');

    if (linkNode) {
      return jQuery(linkNode).attr(attrName);
    }
  }

  getLink() {
    return this._getLinkAttr('href') || '';
  }

  getLinkTarget() {
    return this._getLinkAttr('target') || window.editor.page.getConfigValue('linkTargetDefault');
  }

  getLinkAction() {
    return this._getLinkAttr('data-action') || 'url';
  }

  getPassParamsValue() {
    return this._getLinkAttr('data-params') === 'true';
  }

  _setLinkAttr(attrs) {
    const linkNode = this._froalaEditorCommand('link.get');

    if (linkNode) {
      jQuery(linkNode).attr(attrs);
      this.sendContentChangedEvent();
    }
  }

  setLink(url) {
    this._froalaEditorCommand('link.insert', url);

    // This ensures the data attrs are updated or set to defaults
    this._setLinkAttr({
      'data-action': this.getLinkAction(),
      'data-params': this.getPassParamsValue(),
      target: this.getLinkTarget(),
    });
  }

  setLinkTarget(target) {
    this._setLinkAttr({target: target});
  }

  setLinkAction(actionType) {
    this._setLinkAttr({'data-action': actionType});
  }

  setPassParams(passParams) {
    this._setLinkAttr({'data-params': passParams});
  }

  removeLink() {
    this._froalaEditorCommand('link.remove');
    this.sendContentChangedEvent();
  }

  setFontSize(fontSize) {
    this._set('fontSize.apply', `${fontSize}px`);
    this.sendContentChangedEvent();
  }

  setParagraphStyle(paragraphStyle) {
    this._set('paragraphFormat.apply', paragraphStyle);

    const $blockElements = this._getBlockElementsForSet();
    $blockElements.removeAttr('style');
    if (paragraphStyle === 'blockquote') {
      // The UI will not show that italic is enabled because it looks for an <em> tag rather
      // than `font-style: italic`.
      // TODO: Add an <em> tag instead or apply the italic as the default blockquote style in
      // page-defaults.css.
      $blockElements.css({'font-style': 'italic', 'font-size': '12px'});
    }

    this.sendContentChangedEvent();
  }

  setFontFamily(fontFamily) {
    this._set('fontFamily.apply', fontFamily);
    this.sendContentChangedEvent();
  }

  indent() {
    this._set('commands.indent');
  }

  outdent() {
    this._set('commands.outdent');
  }

  insertHorizontalLine() {
    this._froalaEditorCommand('commands.insertHR');
  }

  setColor(color) {
    this._set('format.applyStyle', 'color', color);
    this.sendContentChangedEvent();
  }

  setFontBackgroundColor(color) {
    this._set('format.applyStyle', 'background-color', color);
    this.sendContentChangedEvent();
  }

  setFontWeight(weight) {
    this._set('format.applyStyle', 'font-weight', String(weight));
    this.sendContentChangedEvent();
  }

  setFontStyle(style) {
    this._set('format.applyStyle', 'font-style', style || 'normal');
    this.sendContentChangedEvent();
  }

  getTextStyles() {
    const $activeElement = this._getActiveElement();

    return ['strong', 'em', 'u', 'sub', 'sup', 's'].reduce((textStyles, tagName) => ({
      ...textStyles,
      [tagName]: $activeElement.closest(tagName, this.froalaEditorContents).length > 0,
    }), {});
  }

  getFontWeight() {
    const fontWeightStr = this._getCssValue('fontWeight');
    const keywordWeight = {
      normal: 400,
      bold: 700,
      bolder: 700,
      lighter: 100,
    }[fontWeightStr];

    return keywordWeight || parseInt(fontWeightStr, 10) || null;
  }

  getFontStyle() {
    const fontStyle = this._getCssValue('fontStyle');
    return !fontStyle || fontStyle === 'normal' ? null : fontStyle;
  }

  getAlignment() {
    return this._getCssValueFromBlockElement('textAlign');
  }

  getListStyle() {
    // Starting from the active element, go up the tree looking for an <ol> or <ul> element,
    // stopping at the Froala view element.
    const $listAncestor = this._getActiveElement().closest('ol,ul', this.froalaEditorContents);
    return $listAncestor[0] ? $listAncestor[0].nodeName.toLowerCase() : null;
  }

  getLineHeight() {
    const lineHeightStr = this._getCssValueFromBlockElement('lineHeight');
    return parseInt(lineHeightStr, 10) || null;
  }

  getComputedLineHeight() {
    return parseInt(this._getBlockElementsForGet().css('lineHeight'), 10) || null;
  }

  getFontFamily() {
    const fontFamily = this._getCssValue('fontFamily');
    return fontFamily ? fontFamily.replace(/'|"/g, '') : null;
  }

  getFontSize() {
    const fontSizeStr = this._getCssValue('fontSize');
    return parseInt(fontSizeStr, 10) || null;
  }

  getComputedFontSize() {
    return parseInt(this._getActiveElement().css('fontSize'), 10) || null;
  }

  getParagraphStyle() {
    const blockElement = this._getBlockElementsForGet()[0];
    return blockElement && paragraphStyles.includes(blockElement.nodeName.toLowerCase())
      ? blockElement.nodeName.toLowerCase()
      : null;
  }

  getColor() {
    const color = this._getCssValue('color');
    return color ? tinycolor2(color).toRgb() : null;
  }

  getFontBackgroundColor() {
    const backgroundColor = this._getCssValue('backgroundColor');
    return backgroundColor ? tinycolor2(backgroundColor).toRgb() : null;
  }

  setHtml(html) {
    this._froalaEditorCommand('html.set', imgCleaner(html));
    this._froalaEditorCommand('html.set', backgroundStyleCleaner(html));
    manualFontSetter(html);

    this.sendContentChangedEvent();
  }

  getHtml() {
    return this._froalaEditorCommand('html.get');
  }
}



// WEBPACK FOOTER //
// ./src/ui/inline-text/APIs/inline-text-style-api.js